<script setup lang="ts">
import { Form, Head } from '@inertiajs/vue3';
import InputError from '@/components/InputError.vue';
import PasswordInput from '@/components/PasswordInput.vue';
import TextLink from '@/components/TextLink.vue';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { login } from '@/routes';
import { store } from '@/routes/register';

defineOptions({
    layout: {
        title: 'Creez votre compte',
        description: 'Renseignez vos informations pour ouvrir votre acces',
    },
});
</script>

<template>
    <Head title="Inscription" />

    <Form
        v-bind="store.form()"
        :reset-on-success="['password', 'password_confirmation']"
        v-slot="{ errors, processing }"
        class="flex flex-col gap-8"
    >
        <div class="grid gap-5">
            <div
                class="rounded-2xl border border-brand/15 bg-brand/[0.04] px-4 py-3 text-sm text-muted-foreground"
            >
                Utilisez l'adresse email que vous souhaitez associer a votre
                espace de revue et a vos invitations.
            </div>

            <div class="grid gap-5 sm:grid-cols-2">
                <div class="grid gap-2.5">
                    <Label for="first_name">Prenom</Label>
                    <Input
                        id="first_name"
                        type="text"
                        required
                        autofocus
                        :tabindex="1"
                        autocomplete="given-name"
                        name="first_name"
                        placeholder="Prenom"
                        class="h-11 rounded-xl border-border/70 bg-background/70 px-4 shadow-none focus-visible:border-brand/40 focus-visible:ring-brand/15"
                    />
                    <InputError :message="errors.first_name" />
                </div>

                <div class="grid gap-2.5">
                    <Label for="last_name">Nom</Label>
                    <Input
                        id="last_name"
                        type="text"
                        required
                        :tabindex="2"
                        autocomplete="family-name"
                        name="last_name"
                        placeholder="Nom"
                        class="h-11 rounded-xl border-border/70 bg-background/70 px-4 shadow-none focus-visible:border-brand/40 focus-visible:ring-brand/15"
                    />
                    <InputError :message="errors.last_name" />
                </div>
            </div>

            <div class="grid gap-2.5">
                <Label for="email">Adresse email</Label>
                <Input
                    id="email"
                    type="email"
                    required
                    :tabindex="3"
                    autocomplete="email"
                    name="email"
                    placeholder="email@example.com"
                    class="h-11 rounded-xl border-border/70 bg-background/70 px-4 shadow-none focus-visible:border-brand/40 focus-visible:ring-brand/15"
                />
                <InputError :message="errors.email" />
            </div>

            <div class="grid gap-2.5">
                <Label for="password">Mot de passe</Label>
                <PasswordInput
                    id="password"
                    required
                    :tabindex="4"
                    autocomplete="new-password"
                    name="password"
                    placeholder="Mot de passe"
                    class="h-11 rounded-xl border-border/70 bg-background/70 px-4 shadow-none focus-visible:border-brand/40 focus-visible:ring-brand/15"
                />
                <InputError :message="errors.password" />
            </div>

            <div class="grid gap-2.5">
                <Label for="password_confirmation"
                    >Confirmer le mot de passe</Label
                >
                <PasswordInput
                    id="password_confirmation"
                    required
                    :tabindex="5"
                    autocomplete="new-password"
                    name="password_confirmation"
                    placeholder="Confirmer le mot de passe"
                    class="h-11 rounded-xl border-border/70 bg-background/70 px-4 shadow-none focus-visible:border-brand/40 focus-visible:ring-brand/15"
                />
                <InputError :message="errors.password_confirmation" />
            </div>

            <Button
                type="submit"
                variant="brand"
                size="lg"
                class="mt-2 w-full rounded-full"
                tabindex="6"
                :disabled="processing"
                data-test="register-user-button"
            >
                <Spinner v-if="processing" />
                Creer mon acces
            </Button>
        </div>

        <div
            class="border-t border-border/60 pt-5 text-center text-sm text-muted-foreground"
        >
            Vous avez deja un compte ?
            <TextLink
                :href="login()"
                class="ml-1 text-brand decoration-brand/30 hover:decoration-brand"
                :tabindex="7"
            >
                Se connecter
            </TextLink>
        </div>
    </Form>
</template>
